'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { type BundlePreview, computeBundlePreview } from './bundle-pricing';
import type { BundleSettings } from './bundle-types';
export interface BundleEntry {
  variantId: string;
  productId: string;
  handle: string;
  title: string;
  vendor?: string;
  productType?: string;
  variantTitle?: string;
  selectedOptions: {
    name: string;
    value: string;
  }[];
  unitAmount: number;
  currencyCode: string;
  sku?: string | null;
  image: {
    url: string;
    altText?: string | null;
    width?: number;
    height?: number;
  } | null;
}
interface BundleContextValue {
  entries: BundleEntry[];
  settings: BundleSettings;
  moneyLocale: string;
  preview: BundlePreview;
  isProductInBundle: (productId: string) => boolean;
  getProductEntry: (productId: string) => BundleEntry | undefined;
  addVariant: (entry: BundleEntry) => void;
  removeVariant: (variantId: string) => void;
  removeProduct: (productId: string) => void;
  clear: () => void;
}
const BundleContext = createContext<BundleContextValue | null>(null);
export function useBundle() {
  const context = useContext(BundleContext);
  if (!context) {
    throw new Error("useBundle must be used within a BundleProvider");
  }
  return context;
}
export function BundleProvider(t0) {
  const $ = _c(23);
  const {
    settings,
    moneyLocale,
    fallbackCurrencyCode,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [entries, setEntries] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = entry => {
      setEntries(prev => {
        if (prev.some(existing => existing.variantId === entry.variantId)) {
          return prev;
        }
        return [...prev, entry];
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const addVariant = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = variantId => {
      setEntries(prev_0 => prev_0.filter(entry_0 => entry_0.variantId !== variantId));
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const removeVariant = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = productId => {
      setEntries(prev_1 => prev_1.filter(entry_1 => entry_1.productId !== productId));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const removeProduct = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setEntries([]);
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const clear = t5;
  let t6;
  if ($[5] !== entries) {
    t6 = productId_0 => entries.some(entry_2 => entry_2.productId === productId_0);
    $[5] = entries;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isProductInBundle = t6;
  let t7;
  if ($[7] !== entries) {
    t7 = productId_1 => entries.find(entry_3 => entry_3.productId === productId_1);
    $[7] = entries;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const getProductEntry = t7;
  let t8;
  if ($[9] !== entries || $[10] !== fallbackCurrencyCode || $[11] !== settings.tiers) {
    const subtotal = entries.reduce(_temp, 0);
    const currencyCode = entries[0]?.currencyCode ?? fallbackCurrencyCode;
    t8 = computeBundlePreview(entries.length, subtotal, currencyCode, settings.tiers);
    $[9] = entries;
    $[10] = fallbackCurrencyCode;
    $[11] = settings.tiers;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const preview = t8;
  let t9;
  if ($[13] !== entries || $[14] !== getProductEntry || $[15] !== isProductInBundle || $[16] !== moneyLocale || $[17] !== preview || $[18] !== settings) {
    t9 = {
      entries,
      settings,
      moneyLocale,
      preview,
      isProductInBundle,
      getProductEntry,
      addVariant,
      removeVariant,
      removeProduct,
      clear
    };
    $[13] = entries;
    $[14] = getProductEntry;
    $[15] = isProductInBundle;
    $[16] = moneyLocale;
    $[17] = preview;
    $[18] = settings;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const value = t9;
  let t10;
  if ($[20] !== children || $[21] !== value) {
    t10 = <BundleContext.Provider value={value}>{children}</BundleContext.Provider>;
    $[20] = children;
    $[21] = value;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp(sum, entry_4) {
  return sum + entry_4.unitAmount;
}